import { Children, cloneElement, isValidElement, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import mapStyles from '../mapStyles';
import css from './MapInternal.css';
import { useCameraAnimation } from './useCameraAnimation';

interface MapInternalProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  children: ReactNode;
  onDragEnd?: () => void;
  onIdle?: (map: google.maps.Map) => void;
  onLoad?: (map: google.maps.Map) => void;
}

const MapInternal = ({ center, children, onDragEnd, onIdle, onLoad, zoom }: MapInternalProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>(null);
  const camera = useCameraAnimation(map, center, zoom);

  useEffect(() => {
    if (ref.current && !map) {
      const googleMap = new window.google.maps.Map(ref.current, { ...mapStyles, center, zoom });

      setMap(googleMap);

      if (onLoad) {
        onLoad(googleMap);
      }
    }
  }, [map, ref]);

  useEffect(() => {
    if (map) {
      ['dragend', 'idle'].forEach((eventName) => google.maps.event.clearListeners(map, eventName));

      if (onDragEnd) {
        map.addListener('dragend', onDragEnd);
      }

      if (onIdle) {
        map.addListener('idle', () => {
          // Ignore idle events fired mid-animation — otherwise the parent writes
          // the map's transient pose back into state, which cancels and aborts
          // the animation partway (lands at the wrong zoom).
          if (camera.isAnimating()) {
            return;
          }

          onIdle(map);
          // After the first settled idle, the initial framing is done and
          // subsequent center/zoom changes are user-driven — safe to animate.
          camera.markInitialized();
        });
      }
    }
  }, [map, onDragEnd, onIdle]);

  return (
    <>
      <div ref={ref} className={css.element} />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child as ReactElement<{ map: typeof map }>, { map });
        }
      })}
    </>
  );
};

export default MapInternal;
